

/*

Form Table: 
name: formAnswers
columns:


- formSubmissionId (the id of that form submission, e.g. "SUB12345", to group answers from the same form submission together if same user submits multiple forms)

- formName (unique identifier for the form, e.g. "SATISFACTION")
- formVersion (to allow for changes in the form over time, e.g. "1.0", "2.0")

- questionId (unique identifier for the question, e.g. "SATISFACTION_Q1")
- questionNumber (the number of the question in the form, e.g. 1 for the first question)
- questionText (the text of the question, e.g. "How satisfied are you with our service?")

- formformAnswerId (primary key)
- answerValueType (e.g. "number", "text", "boolean")
- answerValueNumber (the numeric value of the answer, if answerValueType is "number")
- answerValueText (the text value of the answer, if answerValueType is "text")
- answerValueBoolean (the boolean value of the answer, if answerValueType is "boolean")
- answerMaxValue (optional, e.g. 5 for a 5 star rating)
- answerMinValue (optional, e.g. 1 for a 5 star rating)

- customerId
- companyName

- userId (foreign key to users table)
- userDisplayName
- userEmail
- userType (derived from permissions)
- userNumberOfVenues

*/

import { getUuid } from "../../scripts/common";
import { saveFormAnswer } from "../../scripts/formAnswer";
import { entityTypes } from "../permission/permissions";

const USER_TYPES = {
    READ_ONLY: "Read Only",
    BASIC: "Basic",
    VENUE_ADMIN: "Venue Admin",
    INSTALLER: "Installer",
}

class Form {
    constructor(formName, formVersion, formTitle, userContext) {
        this.verrifyDetails(formName, formVersion, userContext);

        this.formSubmissionId = getUuid();
        this.formName = formName;
        this.formVersion = formVersion;
        this.formTitle = formTitle;
        this.customerId = userContext.customer.customerId || null;
        this.companyName = userContext.customer.companyName || null; 
        this.userId = userContext.user.userId || null;
        this.userDisplayName = this.getDisplayName(userContext);
        this.userEmail = userContext.user.email || null;
        this.userType = this.getHighestUserType(userContext);
        this.userNumberOfVenues = this.getNumberOfVenues(userContext);

    }

    verrifyDetails(formName, formVersion, userContext) {
        if (!formName) throw new Error('formName is required');
        if (!formVersion) throw new Error('formVersion is required');
        if (!userContext?.user) throw new Error('userContext is required');
        if (!userContext?.user?.userId) throw new Error('userContext.user.userId is required');
        if (!userContext?.permissions) throw new Error('userContext.permissions is required');
        if (!userContext?.customer) throw new Error('userContext.customer is required');
    }

    getDisplayName(userContext) {
        const user = userContext?.user;

        const { firstName, lastName, username} = user;
        if (firstName && lastName) return `${firstName} ${lastName}`;
        else if (firstName) return firstName;
        else if (lastName) return lastName;
        else return username || null;
    }

    getHighestUserType = (userContext) => {
        const permissions = userContext?.permissions || [];
        if (!permissions || permissions.length === 0) return null;

        // check for a permissionType thet includes '.write', if not --> return "Basic Read"
        const hasWritePermission = permissions.some(permission => permission.permissionType?.includes(".write"));
        if (!hasWritePermission) return USER_TYPES.READ_ONLY;

        // check if permissions include userAdministration.write --> return "Venue Admin"
        const hasVenueAdminPermission = permissions.some(permission => permission.permissionType === "userAdministration.write");
        if (hasVenueAdminPermission) return USER_TYPES.VENUE_ADMIN;

        // check if permissions include installationView.write --> return "Installer"
        const hasInstallerPermission = permissions.some(permission => permission.permissionType === "installationView.write");
        if (hasInstallerPermission) return USER_TYPES.INSTALLER;

        // if permissions include any write permissions --> return "Basic Write"
        return USER_TYPES.BASIC;
    }

    getNumberOfVenues = (userContext) => {
        const permissions = userContext?.permissions || [];
        if (!Array.isArray(permissions) || permissions.length === 0) return 0;

        const uniqueVenueIds = new Set(
            permissions
            .filter(permission => permission.entityType === entityTypes.venue)
            .map(permission => permission.entityId)
        );

        return uniqueVenueIds.size;
    }
}

class FormAnswer {
    constructor(form, questionId, questionNumber, questionText, answerValueType, answerMaxValue=null, answerMinValue=null) {

        this.checkRequiredFields(form, questionId, questionNumber, questionText, answerValueType);

        this.addFormDetails(form);
        this.formAnswerId = getUuid();
        this.questionId = questionId;
        this.questionNumber = questionNumber;
        this.questionText = questionText;
        this.answerValueType = answerValueType;
        this.answerValueNumber = null;
        this.answerValueText = null;
        this.answerValueBoolean = null;
        this.answerMaxValue = answerMaxValue;
        this.answerMinValue = answerMinValue;
    }

    checkRequiredFields(form, questionId, questionNumber, questionText, answerValueType) {
        if (!form || !(form instanceof Form)) {
            throw new Error('form must be an instance of FormSubmission');
        }
        if (!questionId) {
            throw new Error('questionId is required');
        }
        if (questionNumber === undefined || questionNumber === null || isNaN(questionNumber)) {
            throw new Error('questionNumber is required');
        }
        if (!questionText) {
            throw new Error('questionText is required');
        }
        if (!answerValueType) {
            throw new Error('answerValueType is required');
        }
        if (!form.formTitle) {
            throw new Error('formTitle is required on form');
        }
    }

    addFormDetails(form) {
        if (!form || !(form instanceof Form)) {
            throw new Error('form must be an instance of FormSubmission');
        }
        this.formSubmissionId = form.formSubmissionId;
        this.formName = form.formName;
        this.formVersion = form.formVersion;
        this.formTitle = form.formTitle;
        this.customerId = form.customerId;
        this.companyName = form.companyName;
        this.userId = form.userId;
        this.userDisplayName = form.userDisplayName;
        this.userEmail = form.userEmail;
        this.userType = form.userType;
        this.userNumberOfVenues = form.userNumberOfVenues;
    }

    getPayload() {

        // check that required fields are present
        if (!this.formSubmissionId) throw new Error('formSubmissionId is required');
        if (!this.formName) throw new Error('formName is required');
        if (!this.formVersion) throw new Error('formVersion is required');
        if (!this.formTitle) throw new Error('formTitle is required');
        if (!this.formAnswerId) throw new Error('formAnswerId is required');
        if (!this.questionId) throw new Error('questionId is required');
        if (this.questionNumber === undefined || this.questionNumber === null || isNaN(this.questionNumber)) throw new Error('questionNumber is required');
        if (!this.questionText) throw new Error('questionText is required');
        if (!this.answerValueType) throw new Error('answerValueType is required');

        return {
            formSubmissionId: this.formSubmissionId,
            formName: this.formName,
            formVersion: this.formVersion,
            formAnswerId: this.formAnswerId,
            formTitle: this.formTitle,
            questionId: this.questionId,
            questionNumber: this.questionNumber,
            questionText: this.questionText,
            answerValueType: this.answerValueType,
            answerValueNumber: this.answerValueNumber,
            answerValueText: this.answerValueText,
            answerValueBoolean: this.answerValueBoolean,
            answerMaxValue: this.answerMaxValue,
            answerMinValue: this.answerMinValue,
            customerId: this.customerId,
            companyName: this.companyName,
            userId: this.userId,
            userDisplayName: this.userDisplayName,
            userEmail: this.userEmail,
            userType: this.userType,
            userNumberOfVenues: this.userNumberOfVenues,
        }
    }

    setAnswerValue(answerValue) {

        this.answerValueNumber = null;
        this.answerValueText = null;
        this.answerValueBoolean = null;

        switch(this.answerValueType) {
            case "number":
                if (typeof answerValue !== "number" || Number.isNaN(answerValue)) {
                    throw new Error(`answerValue must be a number for answerValueType "number"`);
                } else if (this.answerMinValue !== null && answerValue < this.answerMinValue) {
                    throw new Error(`answerValue must be >= ${this.answerMinValue}`);
                } else if (this.answerMaxValue !== null && answerValue > this.answerMaxValue) {
                    throw new Error(`answerValue must be <= ${this.answerMaxValue}`);
                }
                this.answerValueNumber = answerValue;
                break;
            case "text":
                if (typeof answerValue !== "string") {
                    throw new Error(`answerValue must be a string for answerValueType "text"`);
                }
                this.answerValueText = answerValue;
                break;
            case "boolean":
                if (typeof answerValue !== "boolean") {
                    throw new Error(`answerValue must be a boolean for answerValueType "boolean"`);
                }
                this.answerValueBoolean = answerValue;
                break;
            default:
                throw new Error(`Invalid answerValueType: ${this.answerValueType}`);
        }
    }

    async save() {
        const payload = this.getPayload();
        if (payload.answerValueBoolean && !payload.answerValueNumber && !payload.answerValueText) return true; // no use saving skipped answers, but return true to allow flow to continue in form
        await saveFormAnswer(payload);
    }
        

}

export { Form, FormAnswer };